import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Flight, GameState, Round } from '@flugwettrennen/shared';
import { routeStats } from '@flugwettrennen/shared';
import { api, ApiError, type LeaderboardEntry } from '../api';
import { FlightTime } from '../components/FlightTime';
import { GoalProgress } from '../components/GoalProgress';
import { Leaderboard } from '../components/Leaderboard';
import { RouteMap } from '../components/RouteMap';
import { hhmm, humanDuration } from '../lib/format';

interface Position {
  lat: number;
  lon: number;
  altitudeFt: number | null;
  groundspeedKt: number | null;
  airborne: boolean;
}

/** Wie oft die Rangliste nachgeladen wird. Reine Datenbankabfrage. */
const LEADERBOARD_INTERVAL_MS = 60000;

/**
 * Wie oft Höhe und Geschwindigkeit nachgeladen werden – nur wenn der Server das
 * Positions-Tracking überhaupt anbietet. Der Abruf kostet API-Kontingent, und
 * ein Flugzeug auf Reiseflughöhe ändert die beiden Werte ohnehin kaum.
 */
const POSITION_INTERVAL_MS = 60000;

export function LiveFlightPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [flight, setFlight] = useState<Flight | null>(null);
  const [pos, setPos] = useState<Position | null>(null);
  /** null = noch nicht bekannt; false = der Server fragt die Position nicht ab. */
  const [tracking, setTracking] = useState<boolean | null>(null);
  const [round, setRound] = useState<Round | null>(null);
  const [gstate, setGstate] = useState<GameState | null>(null);
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [waiting, setWaiting] = useState<string | null>(null);

  const mode = round?.mode ?? 'sim';

  const load = useCallback(async () => {
    try {
      const r = await api.game.live(id);
      setFlight(r.flight);
      setPos(r.position);
      setTracking(r.positionTracking);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) navigate(`/round/${id}`);
    }
  }, [id, navigate]);

  /**
   * Runde und Spielstand. Beides ändert sich während eines Fluges nicht – der
   * Server hängt das Teilstück erst bei der Landung an –, also reicht einmal.
   */
  useEffect(() => {
    api.rounds
      .get(id)
      .then((r) => {
        setRound(r.round);
        setGstate(r.state);
      })
      .catch(() => undefined);
  }, [id]);

  /** Mitspieler kommen währenddessen voran; die Rangliste kostet kein Kontingent. */
  useEffect(() => {
    const code = round?.challengeCode;
    if (!code) return;
    let stopped = false;

    async function refresh() {
      try {
        const board = await api.rounds.challenge(code!);
        if (!stopped) setEntries(board.entries);
      } catch {
        // Netzwerkaussetzer übergehen, der nächste Durchlauf versucht es erneut.
      }
    }

    void refresh();
    const iv = setInterval(() => void refresh(), LEADERBOARD_INTERVAL_MS);
    return () => {
      stopped = true;
      clearInterval(iv);
    };
  }, [round?.challengeCode]);

  /** Im Live-Modus laufen "Unterwegs" und "Landung in" mit der echten Uhr. */
  useEffect(() => {
    if (mode !== 'live') return;
    const iv = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(iv);
  }, [mode]);

  /** Einmal beim Öffnen: der gespeicherte Flug kommt aus unserer Datenbank. */
  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Regelmäßig nachfragen lohnt nur, solange der Server die Position wirklich
   * abruft. Ist das Tracking aus, ändert sich an dieser Antwort nichts mehr –
   * die Landung vollzieht ohnehin die `advance`-Abfrage weiter unten.
   */
  useEffect(() => {
    if (!tracking) return;
    const iv = setInterval(() => void load(), POSITION_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [load, tracking]);

  /**
   * Gelandet wird im Live-Modus zur Ankunftszeit, nicht auf Knopfdruck. Die
   * regelmäßige Abfrage vollzieht die Landung, sobald sie fällig ist, und hält
   * nebenbei die erwarteten Zeiten aktuell, wenn sich der Flug verspätet.
   */
  useEffect(() => {
    if (mode !== 'live') return;
    let stopped = false;

    async function poll() {
      try {
        const res = await api.game.advance(id, true);
        if (stopped) return;
        if (res.selectedFlight) setFlight(res.selectedFlight);
        setWaiting(res.waitingUntil);
        if (res.state.phase === 'finished') navigate(`/result/${id}`);
        else if (res.state.phase === 'boarding') navigate(`/round/${id}`);
      } catch {
        // Netzwerkaussetzer übergehen, der nächste Durchlauf versucht es erneut.
      }
    }

    void poll();
    const iv = setInterval(() => void poll(), 30000);
    return () => {
      stopped = true;
      clearInterval(iv);
    };
  }, [id, mode, navigate]);

  async function land() {
    setWaiting(null);
    const res = await api.game.advance(id);
    if (res.state.phase === 'finished') navigate(`/result/${id}`);
    else if (res.state.phase === 'boarding') navigate(`/round/${id}`);
    else if (res.waitingUntil) setWaiting(res.waitingUntil);
  }

  const stats = useMemo(() => {
    if (!flight) return null;
    return routeStats(gstate?.legs ?? [], { from: flight.origin, to: flight.destination });
  }, [flight, gstate?.legs]);

  if (!flight) return <div className="container page"><div className="spinner" /></div>;

  const arrivalMs = new Date(flight.estimatedArrival ?? flight.scheduledArrival).getTime();
  /**
   * Im Live-Modus vergeht während des Fluges echte Zeit, im Sim-Modus nicht –
   * dort steht die Spieluhr bis zur Landung auf dem Abflug. Ein mitlaufender
   * Zähler wäre im Sim-Modus schlicht falsch.
   */
  const clockMs = gstate ? new Date(gstate.gameClock).getTime() : 0;
  const referenceMs =
    mode === 'live' ? Math.min(Math.max(now, clockMs), arrivalMs) : clockMs;
  const elapsed =
    round && gstate
      ? humanDuration((referenceMs - new Date(round.startDateTime).getTime()) / 60000)
      : null;
  const remainingMin = Math.round((arrivalMs - (mode === 'live' ? now : clockMs)) / 60000);
  const remaining = humanDuration(remainingMin);
  const showLeaderboard = entries.length > 1;

  return (
    <div className="container page stack">
      <div className="board" style={{ maxWidth: 640, margin: '0 auto' }}>
        <div className="board-head">
          <span className="dot" />
          <span>{t('live.title')}</span>
          <span className="spacer" style={{ flex: 1 }} />
          <span className="mono">{flight.flightNumber}</span>
        </div>
        <div style={{ padding: 24, color: 'var(--board-ink)', fontFamily: 'var(--font-mono)' }}>
          <div className="live-route">
            <span className="code">{flight.origin.iata}</span>
            <span className="plane">✈</span>
            <span className="code to">{flight.destination.iata}</span>
            <span className="place">{flight.origin.name}</span>
            <span />
            <span className="place to">{flight.destination.name}</span>
          </div>
          <hr style={{ border: 'none', borderTop: '1px solid var(--board-line)', margin: '18px 0' }} />
          <dl className="detail-grid" style={{ margin: 0 }}>
            <div><dt style={{ color: 'var(--board-dim)', fontSize: '0.72rem' }}>{t('live.departure')}</dt><dd style={{ margin: 0 }}><FlightTime scheduled={flight.scheduledDeparture} estimated={flight.estimatedDeparture} delayMinutes={flight.delayMinutes} /></dd></div>
            <div><dt style={{ color: 'var(--board-dim)', fontSize: '0.72rem' }}>{t('live.arrival')}</dt><dd style={{ margin: 0 }}><FlightTime scheduled={flight.scheduledArrival} estimated={flight.estimatedArrival} /></dd></div>
            {tracking && (
              <>
                <div data-testid="live-altitude"><dt style={{ color: 'var(--board-dim)', fontSize: '0.72rem' }}>{t('live.altitude')}</dt><dd style={{ margin: 0 }}>{pos?.altitudeFt ? `${pos.altitudeFt} ft` : '—'}</dd></div>
                <div data-testid="live-speed"><dt style={{ color: 'var(--board-dim)', fontSize: '0.72rem' }}>{t('live.speed')}</dt><dd style={{ margin: 0 }}>{pos?.groundspeedKt ? `${pos.groundspeedKt} kt` : '—'}</dd></div>
              </>
            )}
            <div><dt style={{ color: 'var(--board-dim)', fontSize: '0.72rem' }}>Airline</dt><dd style={{ margin: 0 }}>{flight.airlineName}</dd></div>
            <div><dt style={{ color: 'var(--board-dim)', fontSize: '0.72rem' }}>Typ</dt><dd style={{ margin: 0 }}>{flight.aircraftType ?? '—'}</dd></div>
          </dl>
        </div>
      </div>

      <div className="center stack" style={{ maxWidth: 640, margin: '20px auto 0', alignItems: 'center' }}>
        {waiting ? (
          <div className="notice">{t('game.liveWaitLive')} ({hhmm(waiting)})</div>
        ) : (
          <p className="muted">{mode === 'live' && tracking ? t('live.trackHint') : ''}</p>
        )}
        <button className="btn btn-go" onClick={() => void land()} data-testid="land-flight">
          {mode === 'sim' ? t('game.fastForwardArr') : t('live.landed')}
        </button>
      </div>

      {stats && (
        <div className="stat-row" data-testid="live-stats">
          <div className="card center">
            <div className="eyebrow">{t('live.flights')}</div>
            <div className="num" data-testid="stat-flights">{stats.flights}</div>
          </div>
          <div className="card center">
            <div className="eyebrow">{t('live.airports')}</div>
            <div className="num" data-testid="stat-airports">{stats.airports}</div>
          </div>
          <div className="card center">
            <div className="eyebrow">{t('live.distance')}</div>
            <div className="num" data-testid="stat-distance">
              {Math.round(stats.distanceKm).toLocaleString(i18n.language)} km
            </div>
          </div>
          {elapsed && (
            <div className="card center">
              <div className="eyebrow">{t('live.elapsed')}</div>
              <div className="num" data-testid="stat-elapsed">
                {t('common.duration', { h: elapsed.h, m: elapsed.m })}
              </div>
            </div>
          )}
          <div className="card center">
            <div className="eyebrow">{t('live.remaining')}</div>
            <div className="num" data-testid="stat-remaining">
              {remainingMin <= 0 ? t('live.landingNow') : t('common.duration', { h: remaining.h, m: remaining.m })}
            </div>
          </div>
        </div>
      )}

      {round && gstate && (
        <div className={showLeaderboard ? 'grid-2' : undefined} style={{ alignItems: 'start' }}>
          <div className="card stack" data-testid="live-goal">
            <div className="eyebrow">{t('live.progressTitle')}</div>
            <p className="muted" style={{ margin: 0, fontSize: '0.88rem' }}>
              {round.goalTypes.length === 1 ? t(`goals.desc.${round.goalTypes[0]}`) : t('game.allGoalsHint')}
            </p>
            <GoalProgress progress={gstate.goalProgress} />
          </div>

          {showLeaderboard && (
            <div className="card stack" data-testid="live-leaderboard">
              <div className="eyebrow">{t('challenge.leaderboard')}</div>
              <Leaderboard entries={entries} compact />
            </div>
          )}
        </div>
      )}

      {round && gstate && (
        <div className="stack" data-testid="live-map">
          <div className="eyebrow">{t('live.routeTitle')}</div>
          <RouteMap
            legs={gstate.legs}
            pending={{ from: flight.origin, to: flight.destination }}
          />
          <p className="muted" style={{ margin: 0, fontSize: '0.8rem' }}>{t('live.routeHint')}</p>
        </div>
      )}
    </div>
  );
}
